import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import './styles/global.scss';
import './styles/theme.scss';

import isEmpty from 'lodash/isEmpty';
import toLower from 'lodash/toLower';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import Compose from './containers/Compose/Compose';
import Messages from './containers/Messages/Messages';
import Reply from './containers/Reply/Reply';
import View from './containers/View/View';
import {CUSTOMER_TYPE_CORE, envNames, MUTUAL_FUND} from './utils/constants';
import machine from './utils/states';
import useMachine from './utils/useMachine';
import { useModal } from './utils/useModal';
import { ANALYTICS_MAP, dispatchPageLoad } from './utils/utils';
import WebViewContext from './utils/webview-context';

const renderStates = {
  COMPOSE: Compose,
  INBOX: Messages,
  REPLY: Reply,
  // eslint-disable-next-line react/display-name
  SENT: props => <View {...props} viewSent />,
  VIEW: View
};

// eslint-disable-next-line
const MessageCenter = ({
  accountTypes = [MUTUAL_FUND],
  apiKey = process.env.REACT_APP_API_KEY,
  baseUrl,
  customerType = CUSTOMER_TYPE_CORE,
  env = envNames.PROD,
  hasTracOnly = false,
  showCompose = false,
  showInboxHeading = true,
  theme,
  webViewEnabled = false // eslint-disable-line
}) => {
  const initialState = showCompose ? 'COMPOSE' : 'INBOX';

  const [inboxMessageCache, updateInboxMessageCache] = useState([]);
  const [sentMessageCache, updateSentMessageCache] = useState([]);
  const [requestStatus, setRequestStatus] = useState(-1);
  const [messageFromId, setMessageFromId] = useState('');
  const [navigationState, transition, additionalState] = useMachine(
    machine,
    initialState
  );

  const { Modal, openModal } = useModal(apiKey, updateSentMessageCache, env);

  const Component = renderStates[navigationState];

  useEffect(() => {
    const selectedMsg = additionalState && additionalState['selectedMessage'];
    const analyticsKey =
      !isEmpty(selectedMsg) && navigationState === 'SENT'
        ? 'view_sent'
        : toLower(navigationState);
    if (ANALYTICS_MAP[analyticsKey]) {
      dispatchPageLoad(ANALYTICS_MAP[analyticsKey]);
    }
  }, [navigationState]);

  let additionalProps;
  if (navigationState === 'INBOX' || navigationState === 'SENT') {
    additionalProps = {
      inboxMessageCache,
      sentMessageCache,
      setMessageFromId,
      updateInboxMessageCache,
      updateSentMessageCache
    };
  }

  return (
    <WebViewContext.Provider value={webViewEnabled}>
      <div className={theme && `theme-${theme}`}>
        <Modal
          modalBodyProps={{
            baseUrl,
            navigationState,
            setRequestStatus,
            theme,
            transition,
            updateInboxMessageCache
          }}
        />
        <Component
          accountTypes={accountTypes}
          additionalState={additionalState}
          apiKey={apiKey}
          baseUrl={baseUrl}
          customerType={customerType}
          env={env}
          hasTracOnly={hasTracOnly}
          messageFromId={messageFromId}
          openModal={openModal}
          requestStatus={requestStatus}
          setRequestStatus={setRequestStatus}
          showInboxHeading={showInboxHeading}
          theme={theme}
          transition={transition}
          {...additionalProps}
          // TODO: Use spread version of additional state everywhere
          {...additionalState}
        />
      </div>
    </WebViewContext.Provider>
  );
};

MessageCenter.propTypes = {
  accountTypes: PropTypes.arrayOf(PropTypes.number),
  apiKey: PropTypes.string,
  baseUrl: PropTypes.string,
  customerType: PropTypes.number,
  env: PropTypes.string,
  hasTracOnly: PropTypes.bool,
  showCompose: PropTypes.bool,
  showInboxHeading: PropTypes.bool,
  theme: PropTypes.oneOf(['ak529', 'md529']),
  webViewEnabled: PropTypes.bool
};

export default MessageCenter;
